/**
 * PartnershipStatusesPage - Страница управления статусами партнёрства организаций
 *
 * Доступ: Admin, Manager
 * 8 системных статусов (из Parser) + пользовательские с кастомным цветом.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Title,
  Button,
  Group,
  Table,
  Text,
  Badge,
  ActionIcon,
  Menu,
  Loader,
  Center,
  Box,
  Stack,
} from '@mantine/core';
import {
  IconPlus,
  IconDots,
  IconEye,
  IconPencil,
  IconTrash,
  IconTags,
  IconCheck,
  IconLock,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import StatusFormModal from '../components/PartnershipStatuses/StatusFormModal';
import tokens from '../theme/designTokens';

/**
 * API функции для статусов партнёрства
 */
const statusesApi = {
  async getStatuses(includeInactive = true) {
    const response = await fetch(`/api/partnership-statuses?include_inactive=${includeInactive}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить статусы');
    }
    return response.json();
  },

  async deleteStatus(id) {
    const response = await fetch(`/api/partnership-statuses/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось удалить статус');
    }
  },
};

/**
 * Форматирование даты
 */
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

const PartnershipStatusesPage = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Модальное окно формы
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);

  // Загрузка статусов
  const loadStatuses = useCallback(async () => {
    try {
      setLoading(true);
      const data = await statusesApi.getStatuses(true);
      setStatuses(data || []);
    } catch (err) {
      console.error('Failed to load statuses:', err);
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  // Открыть форму создания
  const handleCreate = () => {
    setEditingStatus(null);
    setFormModalOpen(true);
  };

  // Открыть форму редактирования
  const handleEdit = (status) => {
    setEditingStatus(status);
    setFormModalOpen(true);
  };

  // Удалить статус
  const handleDelete = async (status) => {
    if (status.is_system) {
      notifications.show({
        title: 'Ошибка',
        message: 'Нельзя удалить системный статус',
        color: 'red',
      });
      return;
    }

    if (!window.confirm(`Удалить статус "${status.name}"?`)) {
      return;
    }

    try {
      await statusesApi.deleteStatus(status.id);
      notifications.show({
        title: 'Успешно',
        message: 'Статус удалён',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      await loadStatuses();
    } catch (err) {
      console.error('Failed to delete status:', err);
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    }
  };

  // После сохранения формы
  const handleFormSuccess = () => {
    setFormModalOpen(false);
    setEditingStatus(null);
    loadStatuses();
  };

  return (
    <Box px="md" py="md">
      <Stack gap="md">
        {/* Заголовок */}
        <Group justify="space-between" align="flex-end">
          <Box>
            <Title order={2} style={{ color: tokens.colors.brand.primary }}>
              Статусы организаций
            </Title>
            <Text c="dimmed" size="sm">
              Справочник статусов партнёрства для организаций
            </Text>
          </Box>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreate}
          >
            Добавить статус
          </Button>
        </Group>

        {/* Таблица */}
        {loading ? (
          <Center py="xl">
            <Loader size="md" />
          </Center>
        ) : statuses.length === 0 ? (
          <Center py="xl">
            <Box style={{ textAlign: 'center' }}>
              <IconTags
                size={48}
                color={tokens.colors.neutral[4]}
                style={{ marginBottom: '16px' }}
              />
              <Text size="lg" c="dimmed" mb="xs">
                Статусы не найдены
              </Text>
              <Text size="sm" c="dimmed" mb="md">
                Создайте статусы для назначения организациям
              </Text>
              <Button
                variant="light"
                leftSection={<IconPlus size={16} />}
                onClick={handleCreate}
              >
                Добавить статус
              </Button>
            </Box>
          </Center>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 60 }}>Порядок</Table.Th>
                <Table.Th style={{ width: 150 }}>Код</Table.Th>
                <Table.Th style={{ width: 250 }}>Название</Table.Th>
                <Table.Th style={{ width: 150 }}>Цвет</Table.Th>
                <Table.Th style={{ width: 120 }}>Тип</Table.Th>
                <Table.Th style={{ width: 100 }}>Статус</Table.Th>
                <Table.Th style={{ width: 180 }}>Создан</Table.Th>
                <Table.Th style={{ width: 60 }}>Действия</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {statuses.map((status) => (
                <Table.Tr
                  key={status.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleEdit(status)}
                >
                  <Table.Td>
                    <Text size="sm" c="dimmed">{status.sort_order}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace" c="dimmed">{status.code}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <IconTags size={16} color={tokens.colors.brand.primary} />
                      <Text size="sm" fw={500}>{status.name}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={status.color}
                      variant="filled"
                      radius="xl"
                      size="sm"
                    >
                      {status.name}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {status.is_system ? (
                      <Badge
                        color="violet"
                        variant="filled"
                        radius="xl"
                        size="sm"
                        leftSection={<IconLock size={10} />}
                      >
                        Системный
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="filled" radius="xl" size="sm">
                        Пользовательский
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {status.is_active ? (
                      <Badge color="green" variant="filled" radius="xl" size="sm">
                        Активен
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="filled" radius="xl" size="sm">
                        Неактивен
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {formatDate(status.created_at)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                        <Menu.Item
                          leftSection={<IconPencil size={14} />}
                          onClick={() => handleEdit(status)}
                        >
                          Редактировать
                        </Menu.Item>
                        {!status.is_system && (
                          <>
                            <Menu.Divider />
                            <Menu.Item
                              leftSection={<IconTrash size={14} />}
                              color="red"
                              onClick={() => handleDelete(status)}
                            >
                              Удалить
                            </Menu.Item>
                          </>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      {/* Модальное окно формы */}
      <StatusFormModal
        opened={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setEditingStatus(null);
        }}
        onSuccess={handleFormSuccess}
        status={editingStatus}
      />
    </Box>
  );
};

export default PartnershipStatusesPage;
